// מפענח את הטקסט שמודבק ב"בדיקת מספרים" לרשימת {did, password}, בכל פורמט סביר:
//   0733517274:303030     0733517274 303030     0733517274 - 303030     0733517274,303030
//   073-351-7274 | 303030     +972733517274 303030     303030 0733517274 (סיסמה קודם)
//   DID=0733517274 | Password=303030     DID: 073-351-7274  סיסמה: 303030
//   [2025-09-10 05:53:56] ApiPhone=0527615195 | DID=0733517274 | Password=303030 | תוצאה=הצליח
//   שורה מגיליון (טאבים): שם <tab> 0733517274 <tab> 303030
// בלי lookbehind בביטויים: דפדפנים ישנים (Safari לפני 16.4) זורקים שגיאה כבר בטעינת הקובץ.

// מספר ישראלי: 9-10 ספרות עם 0 בהתחלה (או 972/+972), קבוצות מופרדות ברווח/מקף/נקודה; וגם 1-700/1-800.
// הפיצול לקבוצות קבוע, כדי שהסיסמה שאחרי רווח לא תיבלע לתוך המספר.
const PHONE_PATTERNS = [
  /(?:\+|00)?972[\s.-]?\d{2}[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g,   // 972-73-351-7274
  /(?:\+|00)?972[\s.-]?\d[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g,       // 972-3-123-4567
  /0\d{2}[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g,                       // 073-351-7274
  /0\d[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g,                          // 03-123-4567
  /1[78]00[\s.-]?\d{3}[\s.-]?\d{3}(?!\d)/g                       // 1-700-500-500
];

const TS_PREFIX = /^\s*[[(]?\s*(?:\d{4}-\d{2}-\d{2}|\d{1,2}[./]\d{1,2}[./]\d{2,4})[ T,]+\d{1,2}:\d{2}(?::\d{2})?\s*[\])]?\s*/;
// מפרידים בין מספר לסיסמה: רווח : ; , | טאב = מקף וגרשיים (למשל "0733517274","303030")
const LEADING_SEPS = /^[\s:;,|\t=\-–—/\\"'“”‘’`]+/;
const TRAILING_SEPS = /[\s:;,|\t=\-–—/\\"'“”‘’`]+$/;
const QUOTES = /^["'“”‘’`]+|["'“”‘’`]+$/g;

/** ספרות בלבד, 972 בהתחלה -> 0 */
export function normPhone(v) {
  let d = String(v || '').replace(/\D/g, '');
  if (d.startsWith('00972')) d = d.slice(2);
  if (d.startsWith('972')) d = `0${d.slice(3)}`;
  return d;
}

/** המספר הראשון בטקסט שאינו חלק מרצף ספרות ארוך יותר: { did, start, end } או null */
function findPhone(str) {
  let best = null;
  for (const re of PHONE_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(str)) !== null) {
      if (m.index > 0 && /\d/.test(str[m.index - 1])) { re.lastIndex = m.index + 1; continue; } // באמצע רצף ספרות
      if (!best || m.index < best.start) best = { did: normPhone(m[0]), start: m.index, end: m.index + m[0].length };
      break;
    }
  }
  return best;
}

/** הסיסמה: האסימון הראשון אחרי המפרידים (או האחרון לפני המספר כשהסיסמה באה קודם) */
function takeToken(str, fromEnd) {
  const t = str.replace(fromEnd ? TRAILING_SEPS : LEADING_SEPS, '');
  const m = fromEnd ? t.match(/[^\s,;|\t]+$/) : t.match(/^[^\s,;|\t]+/);
  return m ? m[0].replace(QUOTES, '') : '';
}

const PASS_LABEL = /(?:^|[\s|,;[(])(?:password|passwd|pass|pwd|pw|סיסמה|סיסמא)\s*[=:]\s*(["']?)([^\s|,;"']+)\1/i;
const DID_LABEL = /(?:^|[\s|,;[(])(?:DID|מספר קו|מספר|קו)\s*[=:]\s*/i;

/** שורה אחת -> { did, password } או { error } */
export function parseNumberLine(raw) {
  const line = String(raw || '').trim();
  if (!line) return null;

  const pw = PASS_LABEL.exec(line);
  const lab = DID_LABEL.exec(line);

  let did = null;
  let password = pw ? pw[2] : '';
  let rest = null;

  if (lab) {
    const after = line.slice(lab.index + lab[0].length);
    const ph = findPhone(after);
    if (ph && ph.start <= 2) { did = ph.did; rest = after.slice(ph.end); }
  }
  if (!did) {
    // בלי תווית: מורידים תחילית של תאריך ושעה (גם הם ספרות ונקודותיים) ולוקחים את המספר הראשון
    const body = line.replace(TS_PREFIX, '');
    let ph = findPhone(body);
    if (!ph) return { error: 'לא נמצא מספר קו' };
    // מספר נייד (05x) הוא לא קו של ימות: אם יש אחריו מספר אחר (למשל ApiPhone בלי תווית), הוא הקו
    if (ph.did.startsWith('05')) {
      const next = findPhone(body.slice(ph.end));
      if (next) ph = { did: next.did, start: ph.end + next.start, end: ph.end + next.end };
    }
    did = ph.did;
    rest = body.slice(ph.end);
    if (!password) {
      password = takeToken(rest, false);
      if (!password) {
        // סיסמה לפני המספר: רק אם זה כל מה שלפניו (אחרת זה שם או טקסט אחר ולא סיסמה)
        const before = body.slice(0, ph.start).replace(LEADING_SEPS, '').replace(TRAILING_SEPS, '');
        if (before && !/[\s,;|]/.test(before)) password = before.replace(QUOTES, '');
      }
    }
  } else if (!password) {
    password = takeToken(rest, false);
  }
  if (!password) return { error: 'לא נמצאה סיסמה' };
  return { did, password };
}

/**
 * טקסט מודבק -> { lines, unparsed, duplicates }
 *   lines: [{did, password}] בלי כפילויות (אותו מספר עם סיסמה אחרת נשמר)
 *   unparsed: [{ n, text, reason }] שורות לא ריקות שלא זוהו (n = מספר השורה)
 */
export function parseNumberList(text) {
  const lines = [];
  const unparsed = [];
  const seen = new Set();
  let duplicates = 0;
  String(text || '').split(/\r?\n/).forEach((raw, i) => {
    const r = parseNumberLine(raw);
    if (!r) return;
    if (r.error) { unparsed.push({ n: i + 1, text: raw.trim().slice(0, 120), reason: r.error }); return; }
    const key = `${r.did}:${r.password}`;
    if (seen.has(key)) { duplicates++; return; }
    seen.add(key);
    lines.push(r);
  });
  return { lines, unparsed, duplicates };
}
